/**
 * A widget's unit noun — pluralised and sentence-cased, in one place.
 *
 * `widget_type.unit_noun` is stored singular ("conversation", "booking") and the
 * screens need three shapes of it: the plural for a sentence, the sentence-cased
 * plural for a caption or a column header, and the `UnitNoun` pair `formatQty`
 * takes. Before this file the `-s` and the charAt-uppercase idiom were written out
 * five times between `pages/client/widget/text.ts` and `WidgetUsagePanel.tsx` —
 * two one-line rules with five copies, which is how "conversations" and
 * "Conversation" end up disagreeing on one screen.
 *
 * It lives beside the panel rather than under the page because the panel is the
 * component that needs it and `components/client/` may not import from
 * `pages/`. `pages/client/widget/text.ts` re-exports both for the page's own use,
 * so there is still one definition and one import path per side.
 *
 * **The `-s` plural is deliberate and is not a branch.** A noun that does not take
 * it is a `widget_type` row to fix (`catalog_create_widget_type!`), not an
 * `if` on `widget_type` — see `WidgetPage.tsx`'s header. `unit_noun_plural` on the
 * type is the field that would retire this function; it is reported as missing.
 */

import type { UnitNoun } from '@fareast/shared'

/** "conversation" → "conversations". Where a sentence needs the plural with no count. */
export function pluralOf(noun: string): string {
  return `${noun}s`
}

/* `sentenceCase` moved to `packages/shared/src/format.ts` in session 20. Session 19
   had this copy; session 20 wrote two more, one per worktree, with the same body and
   the same reason. Import it from `@fareast/shared`. */

/** The pair `format.qty()` wants: `{ one: 'conversation', other: 'conversations' }`. */
export function unitNounOf(noun: string): UnitNoun {
  return { one: noun, other: pluralOf(noun) }
}
